import { Controller } from "@hotwired/stimulus"

// The small player on each saved song of TEAM's album edit page
// (DPK_Alert_Routing_Plan.md, owner 2026-10-09: "click on it, check, and
// fix"). Its src is the Rails blob redirect path, never a signed storage
// URL, and nothing loads until Play (preload none).
//
// Why a controller at all: a started song keeps the storage link the
// redirect first gave it (Chrome sends every later byte range to that link,
// never back to the page's own address), and that link dies after five
// minutes. So a song paused, then played or scrubbed after that would be
// dead. Before the link can die this re-arms the element: the redirect path
// is assigned again, the browser asks for a fresh link, and playback carries
// on from the same second. A media "error" is the backstop for the same
// condition, at most once a minute so a dead network cannot loop it. The
// public DPK's player does the same thing (dpk-reader dpk.js, 2026-09-17).
// A song played straight through past five minutes, with no pause and no
// seek, is covered only by that error backstop, on purpose: nothing here
// interrupts a song that is playing.
//
// The re-armed address carries a throwaway "fresh" parameter. Rails answers
// the redirect with Cache-Control max-age of those same five minutes, so the
// bare path asked again inside that window (this re-arms at four minutes) is
// served from the browser's cache: the SAME link, with only its last seconds
// left. The parameter makes the browser ask Rails, and Rails ignores it.
// Pinned by test/system/departments/prt/dpk_alert_pages_journey_test.rb,
// where six minutes pass on the server while the browser's cache still
// holds the old redirect: without the parameter the re-armed song is handed
// the dead link again (measured 2026-10-09).
const STALE_AFTER_MS = 4 * 60 * 1000
const REARM_GAP_MS = 60 * 1000

export default class extends Controller {
  static values = { src: String }

  connect() {
    this.startedAt = null
    this.rearmedAt = 0
    this.rearming = false
    this.onPlay = this.onPlay.bind(this)
    this.onSeeking = this.onSeeking.bind(this)
    this.onError = this.onError.bind(this)
    this.element.addEventListener("play", this.onPlay)
    this.element.addEventListener("seeking", this.onSeeking)
    this.element.addEventListener("error", this.onError)
  }

  disconnect() {
    this.element.removeEventListener("play", this.onPlay)
    this.element.removeEventListener("seeking", this.onSeeking)
    this.element.removeEventListener("error", this.onError)
  }

  onPlay() {
    if (this.rearming) return
    if (this.isStale()) {
      this.rearm(this.element.currentTime, true)
    } else if (this.startedAt === null) {
      this.startedAt = Date.now()
    }
  }

  onSeeking() {
    if (this.rearming || !this.isStale()) return
    this.rearm(this.element.currentTime, !this.element.paused)
  }

  onError() {
    if (this.rearming || this.startedAt === null) return
    if (Date.now() - this.rearmedAt < REARM_GAP_MS) return
    this.rearm(this.element.currentTime, !this.element.paused)
  }

  isStale() {
    return this.startedAt !== null && Date.now() - this.startedAt > STALE_AFTER_MS
  }

  freshSrc() {
    return `${this.srcValue}${this.srcValue.includes("?") ? "&" : "?"}fresh=${Date.now()}`
  }

  // Assign the redirect path again and put the position back. Nothing has
  // been fetched when currentTime is set, so the browser keeps it as the
  // default playback start position and seeks there when the metadata lands
  // (HTML spec; the reader's player relies on the same rule).
  rearm(resumeAt, thenPlay) {
    this.rearming = true
    this.rearmedAt = Date.now()
    this.startedAt = thenPlay ? Date.now() : null
    this.element.src = this.freshSrc()
    this.element.load()
    if (resumeAt > 0) this.element.currentTime = resumeAt
    const done = () => { this.rearming = false }
    if (thenPlay) {
      const playing = this.element.play()
      if (playing && playing.then) playing.then(done, done)
      else done()
    } else {
      done()
    }
  }
}
